import { useEffect, useRef, useState } from 'react';
import { Button, Label, Select, Modal, TextInput } from 'flowbite-react';
import { useAuth } from '../../utils/Auth/AuthProvider';
import { useAddSubjectChapter, useSchoolGradeSubjects, useSchoolGrades, useSubjectChapters, useUserInfo } from '../../react-query/queries';
import SubchapterDialog from '../../components/SchoolSetup/SubchapterDialog';
import { adminGetInstitutionFields, adminGetFieldAcademicYears, adminGetAcademicYearCourses, AdminField, AdminAcademicYear } from '../../utils/remote';

const ChapterSetup = () => {
  const { token, role } = useAuth();
  const isAllowed = !!token && (role === 'superuser' || role === 'school' || role === 'college_university');

  const { data: gradesData } = useSchoolGrades(token);
  const [gradeId, setGradeId] = useState<number | null>(null);
  const { data: subsData } = useSchoolGradeSubjects(token, gradeId ? String(gradeId) : '0');
  const [subjectId, setSubjectId] = useState<number | null>(null);
  // University flow states
  const [fieldsData, setFieldsData] = useState<AdminField[] | null>(null);
  const [academicYears, setAcademicYears] = useState<AdminAcademicYear[] | null>(null);
  const [academicYearId, setAcademicYearId] = useState<number | null>(null);
  const [coursesData, setCoursesData] = useState<{ id: number; name: string }[] | null>(null);
  const { data: userInfo } = useUserInfo();

  // Determine if user's institution is university/college (used to switch flows)
  const isUniversity = (() => {
    const instType = (userInfo?.institution_type || (role === 'college_university' ? 'University/College' : ''));
    const t = String(instType || '').toLowerCase();
    return t.includes('university') || t.includes('college') || role === 'college_university';
  })();

  // Only use the subject-chapters hook for school flow. For university flow we fetch course chapters separately.
  const { data: chaptersData, refetch: refetchChaps, isFetching } = useSubjectChapters(token, isUniversity ? null : subjectId, isUniversity ? null : gradeId);
  const addChapter = useAddSubjectChapter();
  const [newChapter, setNewChapter] = useState('');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isPresetModalOpen, setIsPresetModalOpen] = useState(false);
  const [presetChapters, setPresetChapters] = useState<any[]>([]);
  const [selectedPresetIds, setSelectedPresetIds] = useState<number[]>([]);
  const [courseChapters, setCourseChapters] = useState<any[] | null>(null);
  const [isUploadingJson, setIsUploadingJson] = useState(false);
  const [useChapterOption, setUseChapterOption] = useState(false);

  const [isSubchapterModalOpen, setIsSubchapterModalOpen] = useState(false);
  const [selectedChapterId, setSelectedChapterId] = useState<number | null>(null);
  const [selectedChapterName, setSelectedChapterName] = useState('');
  const [expandedChapterId, setExpandedChapterId] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleJsonFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (subjectId == null) {
      alert('Please select a course before uploading JSON');
      e.currentTarget.value = '';
      return;
    }
    setIsUploadingJson(true);
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      const remote = await import('../../utils/remote');
      if (useChapterOption) {
        await remote.uploadCourseChapterOptionsFromJSON(token, subjectId, parsed);
        // refresh chapter options
        const res = await remote.getCourseChapterOptions(token, subjectId);
        setCourseChapters(res.chapters || []);
      } else {
        await remote.uploadCourseChaptersFromJSON(token, subjectId, parsed);
        // refresh course chapters
        const res = await remote.getCourseChapters(token, subjectId);
        setCourseChapters(res.chapters || []);
      }
      // optional: also refresh legacy chapters hook
      refetchChaps();
    } catch (err) {
      console.error('Failed to upload JSON', err);
      alert('Failed to upload JSON. See console for details.');
    } finally {
      setIsUploadingJson(false);
      e.currentTarget.value = '';
    }
  };

  useEffect(() => {
    // Only auto-select a grade for school flow; for university flow we'll load departments separately
    if (!isUniversity && gradesData?.grades?.length && gradeId == null) setGradeId(gradesData.grades[0].id);
  }, [gradesData, gradeId]);

  useEffect(() => {
    // Only auto-select a subject for school flow
    if (!isUniversity && subsData?.subjects?.length && subjectId == null) setSubjectId(subsData.subjects[0].id);
  }, [subsData, subjectId]);

  useEffect(() => {
    // Only trigger subject-chapters refetch for school flow
    if (!isUniversity && subjectId != null) refetchChaps();
  }, [isUniversity, subjectId, refetchChaps]);

  

  // For university users, load chapters by course (since subjectId represents course)
  useEffect(() => {
    let mounted = true;
    (async () => {
      if (isUniversity && subjectId != null) {
        try {
          const remote = await import('../../utils/remote');
          const res = useChapterOption ? await remote.getCourseChapterOptions(token, subjectId) : await remote.getCourseChapters(token, subjectId);
          if (!mounted) return;
          setCourseChapters(res.chapters || []);
        } catch (e) {
          console.error('Failed to load course chapters', e);
          if (mounted) setCourseChapters([]);
        }
      } else {
        if (mounted) setCourseChapters(null);
      }
    })();
    return () => { mounted = false; }
  }, [isUniversity, subjectId, token]);

  // Load departments (fields) for university users
  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const instId = userInfo?.institution_id ?? null;
        if (isUniversity && instId) {
          const fields = await adminGetInstitutionFields(instId);
          if (!mounted) return;
          setFieldsData(fields || []);
          if ((fields || []).length && gradeId == null) setGradeId((fields[0] && fields[0].id) || null);
        } else {
          if (mounted) setFieldsData(null);
        }
      } catch (e) {
        console.error('Failed to load institution fields', e);
        if (mounted) setFieldsData([]);
      }
    };
    load();
    return () => { mounted = false; };
  }, [isUniversity, userInfo]);

  // When a department is selected, load academic years (do not auto-select a year)
  useEffect(() => {
    let mounted = true;
    const instId = userInfo?.institution_id ?? null;
    if (isUniversity && instId && gradeId) {
      (async () => {
        try {
          const years = await adminGetFieldAcademicYears(instId, gradeId);
          if (!mounted) return;
          setAcademicYears(years || []);
          // clear previously selected academic year and courses
          setAcademicYearId(null);
          setCoursesData(null);
          setSubjectId(null);
        } catch (e) {
          console.error('Failed to load academic years', e);
          if (mounted) setAcademicYears([]);
        }
      })();
    } else {
      if (mounted) setAcademicYears(null);
    }
    return () => { mounted = false; }
  }, [isUniversity, userInfo, gradeId]);

  // When an academic year is selected, load courses for that year
  useEffect(() => {
    let mounted = true;
    const instId = userInfo?.institution_id ?? null;
    if (isUniversity && instId && gradeId && academicYearId) {
      (async () => {
        try {
          const courses = await adminGetAcademicYearCourses(instId, gradeId, academicYearId);
          if (!mounted) return;
          setCoursesData(courses || []);
          // do not auto-select a course; leave subjectId null for explicit user selection
          setSubjectId(null);
        } catch (e) {
          console.error('Failed to load courses for academic year', e);
          if (mounted) setCoursesData([]);
        }
      })();
    } else {
      if (mounted) setCoursesData(null);
    }
    return () => { mounted = false; }
  }, [isUniversity, userInfo, gradeId, academicYearId]);

  if (!isAllowed) return <div className="rounded border border-red-300 bg-red-50 p-4 text-red-700">403 - Not authorized</div>;

  const doAdd = async () => {
    if (!newChapter.trim() || subjectId == null || gradeId == null) return; // Ensure gradeId is also present
    try {
      if (isUniversity && useChapterOption) {
        const remote = await import('../../utils/remote');
        await remote.addCourseChapterOptionApi(token, subjectId, newChapter.trim());
        // refresh option chapters
        const res = await remote.getCourseChapterOptions(token, subjectId);
        setCourseChapters(res.chapters || []);
      } else {
        // mutation expects a single school_grade_id (number) — remote helper will wrap it into an array
        await addChapter.mutateAsync({ toBeValidated: token, subjectId, name: newChapter.trim(), school_grade_id: gradeId });
        refetchChaps();
      }
    } catch (e) {
      console.error('Failed to add chapter', e);
      alert('Failed to add chapter. See console for details.');
    } finally {
      setNewChapter('');
    }
  }

  const handleChapterClick = (chapterId: number, chapterName: string) => {
    setSelectedChapterId(chapterId);
    setSelectedChapterName(chapterName);
    setIsSubchapterModalOpen(true);
  };

  return (
    <div className="w-full bg-white px-5 pt-6 pb-4 shadow-default dark:border-strokedark dark:bg-boxdark">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold">Chapter Setup</h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Choose a grade and subject to manage its chapters.</p>
        </div>
        {/* Show count for chapters matching the selected grade */}
        <span className="rounded-full bg-gray-100 px-3 py-1 text-xs text-gray-700 dark:bg-gray-700 dark:text-gray-200">{(
          (chaptersData?.chapters || []).filter((c: any) => {
            if (!gradeId) return false
            try {
              const gradeArr = Array.isArray(c.school_grades) && c.school_grades.length ? c.school_grades : (Array.isArray(c.school_grade_ids) ? c.school_grade_ids : [])
              const inGrade = Array.isArray(gradeArr) && gradeArr.map((x: any) => Number(x)).includes(Number(gradeId))
              // Ensure current user's institution is in chapter.institutions
              const instId = userInfo?.institution_id
              const instArr = Array.isArray(c.institutions) ? c.institutions : []
              const hasInst = !!instId && instArr.map((x: any) => Number(x)).includes(Number(instId))
              return inGrade && hasInst
            } catch (e) {
              return false
            }
          }).length
        ) ?? 0} chapters</span>
      </div>

        <div className="mt-4 rounded border border-gray-100 p-3 dark:border-gray-700">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <Label htmlFor="gradeSel" value={isUniversity ? 'Department' : 'Grade'} />
            <Select id="gradeSel" value={gradeId ?? ''} onChange={(e) => setGradeId(e.target.value ? Number(e.target.value) : null)}>
              {isUniversity ? (
                (fieldsData || []).map((f: AdminField) => (
                  <option key={f.id} value={f.id}>{f.name}</option>
                ))
              ) : (
                gradesData?.grades?.map((g: any) => (
                  <option key={g.id} value={g.id}>{g.grade_name || g.name}</option>
                ))
              )}
            </Select>
          </div>
          {isUniversity && (
            <div>
              <Label htmlFor="yearSel" value="Academic Year" />
              <Select id="yearSel" value={academicYearId ?? ''} onChange={(e) => setAcademicYearId(e.target.value ? Number(e.target.value) : null)}>
                <option value="">-- Select academic year --</option>
                {(academicYears || []).map((y: AdminAcademicYear) => (
                  <option key={y.id} value={y.id}>{y.name}</option>
                ))}
              </Select>
            </div>
          )}
          <div>
            <Label htmlFor="subSel" value={isUniversity ? 'Course' : 'Subject'} />
            <Select id="subSel" value={subjectId ?? ''} onChange={(e) => setSubjectId(e.target.value ? Number(e.target.value) : null)}>
              {isUniversity ? (
                <>
                  <option value="">-- Select course --</option>
                  {(coursesData || []).map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </>
              ) : (
                <>
                  <option value="">-- Select subject --</option>
                  {subsData?.subjects?.map((s: any) => (
                    <option key={s.id} value={s.id}>{s.subject_name || s.name}</option>
                  ))}
                </>
              )}
            </Select>
          </div>
        </div>
      </div>

      <div className="mt-6">
        <h4 className="mb-2 text-base font-semibold">Chapters</h4>
        <div className="mb-3 flex gap-2">
          <div className="flex items-center gap-3">
            <input id="useOptionToggle" type="checkbox" checked={useChapterOption} onChange={(e) => setUseChapterOption(e.target.checked)} />
            <label htmlFor="useOptionToggle" className="text-sm text-gray-700">Use Chapter Option</label>
          </div>
          <Button size="sm" onClick={() => setIsAddModalOpen(true)}>Add Chapter</Button>
          <Button size="sm" onClick={async () => {
            if (gradeId == null || subjectId == null) return;
            // fetch preset chapters for school or uni presets
            try {
              const presetName = isUniversity ? 'eltest-uni' : 'eltest-school';
              const res = await (await import('../../utils/remote')).getPresetInstitutionGradeChapters(presetName, gradeId, subjectId);
              setPresetChapters(res.chapters || []);
            } catch (e) {
              setPresetChapters([]);
            }
            setSelectedPresetIds([]);
            setIsPresetModalOpen(true);
          }} disabled={gradeId == null || subjectId == null}>Load Preset</Button>
          {isUniversity && (
            <>
              <input ref={fileInputRef} title="Upload JSON" aria-label="Upload JSON" type="file" accept="application/json" className="hidden" onChange={handleJsonFile} />
              <Button size="sm" color="gray" onClick={() => fileInputRef.current?.click()} disabled={subjectId == null || isUploadingJson}>{isUploadingJson ? 'Uploading...' : 'Load from JSON'}</Button>
            </>
          )}
          {/* debug output removed */}
        </div>
        {isFetching ? (
          <p className="text-sm text-gray-500">Loading chapters…</p>
        ) : (
          (() => {
            const allChaps = isUniversity ? (courseChapters || []) : (chaptersData?.chapters || [])
            let filtered: any[] = []
            if (isUniversity) {
              // For university flow, chapters are already fetched by course; ensure they belong to the current institution
              const instId = userInfo?.institution_id
              filtered = (allChaps || []).filter((c: any) => {
                try {
                  if (!instId) return false
                  const instArr = Array.isArray(c.institutions) ? c.institutions : []
                  return instArr.map((x: any) => Number(x)).includes(Number(instId))
                } catch (e) {
                  return false
                }
              })
            } else {
              filtered = (allChaps || []).filter((c: any) => {
                if (!gradeId) return false
                try {
                  const gradeArr = Array.isArray(c.school_grades) && c.school_grades.length ? c.school_grades : (Array.isArray(c.school_grade_ids) ? c.school_grade_ids : [])
                  const inGrade = Array.isArray(gradeArr) && gradeArr.map((x: any) => Number(x)).includes(Number(gradeId))
                  const instId = userInfo?.institution_id
                  const instArr = Array.isArray(c.institutions) ? c.institutions : []
                  const hasInst = !!instId && instArr.map((x: any) => Number(x)).includes(Number(instId))
                  return inGrade && hasInst
                } catch (e) {
                  return false
                }
              })
            }

            if (filtered.length) {
              return (
                <div className="divide-y divide-gray-100 rounded border border-gray-100 dark:divide-gray-700 dark:border-gray-700">
                  {filtered.map((c: any) => (
                        // University flow: render collapsible chapter rows that reveal subchapters when expanded
                        isUniversity ? (
                          <div key={c.id} className="border-b border-gray-100 dark:border-gray-700">
                            <div className="flex items-center justify-between px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700">
                              <div className="flex items-center gap-3 cursor-pointer" onClick={() => setExpandedChapterId(expandedChapterId === c.id ? null : c.id)}>
                                <span className={`inline-block transition-transform ${expandedChapterId === c.id ? 'rotate-90' : ''}`} aria-hidden>
                                  ▶
                                </span>
                                <span className="font-medium">{c.name}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-gray-500">ID: {c.id}</span>
                                <Button size="xs" onClick={(e) => { e.stopPropagation(); handleChapterClick(c.id, c.name); }}>Manage</Button>
                              </div>
                            </div>
                            {expandedChapterId === c.id && (
                              <div className="pl-10 pr-3 pb-3 pt-2">
                                {(c.subchapters || []).length ? (
                                  <ul className="space-y-2">
                                    {c.subchapters.map((sc: any) => (
                                      <li key={sc.id} className="flex items-center justify-between gap-3 rounded-md bg-gray-50 p-2 hover:bg-gray-100 dark:bg-gray-800 dark:hover:bg-gray-700 transition">
                                        <div className="flex items-center gap-3">
                                          <span className="flex h-3 w-3 items-center justify-center rounded-full bg-blue-500 text-white text-xs font-semibold">•</span>
                                          <span className="text-sm font-medium text-gray-800 dark:text-gray-100">{sc.name}</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                          <button
                                            type="button"
                                            className="text-xs text-blue-600 hover:underline"
                                            onClick={(e) => { e.stopPropagation(); setSelectedChapterId(c.id); setSelectedChapterName(c.name); setIsSubchapterModalOpen(true); }}
                                          >
                                            Manage
                                          </button>
                                        </div>
                                      </li>
                                    ))}
                                  </ul>
                                ) : (
                                  <div className="text-sm text-gray-500">No subchapters</div>
                                )}
                              </div>
                            )}
                          </div>
                        ) : (
                          <div key={c.id} className="flex cursor-pointer items-center justify-between px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700" onClick={() => handleChapterClick(c.id, c.name)}>
                            <span className="font-medium">{c.name}</span>
                            <span className="text-xs text-gray-500">ID: {c.id}</span>
                          </div>
                        )
                      ))}
                </div>
              )
            }

            return (
              <div className="rounded border border-dashed border-gray-200 p-4 text-center text-sm text-gray-500 dark:border-gray-700 dark:text-gray-400">No chapters for this subject & grade. Add one above.</div>
            )
          })()
        )}
      </div>

      <SubchapterDialog
        isOpen={isSubchapterModalOpen}
        onClose={() => setIsSubchapterModalOpen(false)}
        chapterId={selectedChapterId}
        chapterName={selectedChapterName}
      />

      <Modal show={isAddModalOpen} onClose={() => setIsAddModalOpen(false)}>
        <Modal.Header>Add Chapter</Modal.Header>
        <Modal.Body>
          <div className="space-y-2">
            <p className="text-sm text-gray-500">For grade: {gradesData?.grades?.find((g: any) => g.id === gradeId)?.grade_name || '-'}, subject: {subsData?.subjects?.find((s: any) => s.id === subjectId)?.subject_name || '-'}</p>
            <Label htmlFor="chapterName" value="Chapter name" />
            <TextInput id="chapterName" value={newChapter} onChange={(e) => setNewChapter(e.target.value)} placeholder="e.g., Introduction" />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={async () => { await doAdd(); setIsAddModalOpen(false); }} isProcessing={addChapter.isPending} disabled={!newChapter.trim() || subjectId == null || gradeId == null}>Save</Button>
          <Button color="gray" onClick={() => setIsAddModalOpen(false)}>Cancel</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={isPresetModalOpen} onClose={() => setIsPresetModalOpen(false)}>
        <Modal.Header>Load Preset Chapters</Modal.Header>
        <Modal.Body>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-500">Preset chapters sourced from 'eltest-school' for the selected grade and subject.</p>
              <div>
                <button
                  type="button"
                  className="text-sm text-blue-600 hover:underline"
                  onClick={() => {
                    const selectable = presetChapters.filter((p) => !(userInfo?.institution_id && Array.isArray(p.institutions) && p.institutions.includes(userInfo.institution_id))).map((p) => p.id);
                    const allSelected = selectable.every((id) => selectedPresetIds.includes(id)) && selectable.length > 0;
                    if (allSelected) {
                      setSelectedPresetIds((s) => s.filter((id) => !selectable.includes(id)));
                    } else {
                      setSelectedPresetIds((s) => Array.from(new Set([...s, ...selectable])));
                    }
                  }}
                >
                  {(() => {
                    const selectable = presetChapters.filter((p) => !(userInfo?.institution_id && Array.isArray(p.institutions) && p.institutions.includes(userInfo.institution_id))).map((p) => p.id);
                    const allSelected = selectable.every((id) => selectedPresetIds.includes(id)) && selectable.length > 0;
                    return allSelected ? 'Unselect all' : 'Select all';
                  })()}
                </button>
              </div>
            </div>
            {presetChapters.length > 0 ? (
              <div className="grid grid-cols-1 gap-2 max-h-64 overflow-y-auto">
                {presetChapters.map((p) => {
                  const already = !!(userInfo?.institution_id && Array.isArray(p.institutions) && p.institutions.includes(userInfo.institution_id));
                  return (
                    <label key={p.id} className={`flex items-center gap-2 p-2 border rounded ${already ? 'bg-gray-50 cursor-not-allowed opacity-60' : 'cursor-pointer'}`}>
                      <input
                        type="checkbox"
                        checked={selectedPresetIds.includes(p.id)}
                        disabled={already}
                        onChange={(e) => {
                          if (e.target.checked) setSelectedPresetIds((s) => [...s, p.id]);
                          else setSelectedPresetIds((s) => s.filter((id) => id !== p.id));
                        }}
                      />
                      <span>{p.name}{already ? ' (already present)' : ''}</span>
                    </label>
                  )
                })}
              </div>
            ) : (
              <div className="text-sm text-gray-500">No preset chapters found for this grade & subject.</div>
            )}
          </div>
        </Modal.Body>
          <Modal.Footer>
          <Button onClick={async () => {
            if (subjectId == null || gradeId == null) return;
            for (const pid of selectedPresetIds) {
              const preset = presetChapters.find((c) => c.id === pid);
              if (!preset) continue;
              try {
                // Prefer joining the existing preset chapter (add institution m2m) instead of creating a duplicate
                const remote = await import('../../utils/remote');
                try {
                  await remote.joinChapter(token, preset.id);
                } catch (joinErr) {
                  // Fallback: if joining fails for some reason, attempt to create a new chapter locally
                  try {
                    await addChapter.mutateAsync({ toBeValidated: token, subjectId, name: preset.name, school_grade_id: gradeId });
                  } catch (createErr) {
                    // ignore and continue
                  }
                }
              } catch (e) {
                // continue with next preset
              }
            }
            setIsPresetModalOpen(false);
            refetchChaps();
          }} isProcessing={addChapter.isPending} disabled={selectedPresetIds.length === 0}>Save</Button>
          <Button color="gray" onClick={() => setIsPresetModalOpen(false)}>Cancel</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ChapterSetup;
